<template>
  <div>
    <DataTable
      title="Campaign URL"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      empty-title="No campaign URLs yet"
      empty-message="A campaign is a hand-picked set of products behind one public link you can share."
      empty-icon="megaphone"
      @page-change="onPage"
      @retry="load"
    >
      <template #actions>
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          outlined
          clearable
          class="kf-inline-search"
          @input="debouncedReload"
        />
        <v-btn small depressed color="primary" class="text-none ml-2" @click="openAdd">
          <LucideIcon name="plus" :size="16" class="mr-1" />
          Add campaign
        </v-btn>
      </template>

      <!-- The id, not the name, is what the marketing team reports against and
           what each lender sees as the LeadID -- so it travels with the name
           rather than hiding in a column someone has to go looking for. -->
      <template #[`item.name`]="{ item }">
        <div>
          <div class="kf-name">{{ item.name }}</div>
          <div class="kf-sub">{{ item.campaign_id }}</div>
        </div>
      </template>

      <template #[`item.product_count`]="{ item }">
        <span :title="(item.products || []).join(', ')">
          {{ item.product_count }}
        </span>
      </template>

      <template #[`item.is_active`]="{ item }">
        <StatusChip :status="item.is_active ? 'Active' : 'Inactive'" small />
      </template>

      <!-- The link is the reason to come back to this screen at all, so it is a
           column rather than only a menu item: the copy button is one click
           from the row, not two. It is the PARTNER link -- the tracked one every
           partner fills in -- not the fixed page URL. -->
      <template #[`item.dynamic_url`]="{ item }">
        <div class="kf-link" @click.stop>
          <span class="kf-link__text" :title="item.dynamic_url">{{ item.dynamic_url }}</span>
          <v-btn icon x-small @click="copyLink(item)">
            <LucideIcon name="copy" :size="14" />
          </v-btn>
        </div>
        <div class="kf-sub">Opens: {{ destinationLabel(item.destination) }}</div>
      </template>

      <template #[`item.created`]="{ item }">
        <span :title="fmt.dateTime(item.created)">{{ fmt.relative(item.created) }}</span>
      </template>

      <!-- The cell stops the click, not the menu items: v-menu detaches its
           content to the app root, so a click inside the menu never reaches
           this row anyway, and .stop on the items cancels VMenu's own close
           handler and leaves the menu hanging open. Same as Products. -->
      <template #[`item.actions`]="{ item }">
        <div class="text-end" @click.stop>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                <LucideIcon name="ellipsis-vertical" :size="18" />
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openEdit(item)">
                <LucideIcon name="pencil" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Edit</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-list-item @click="openPerformance(item)">
                <LucideIcon name="chart-column" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Performance</v-list-item-title>
              </v-list-item>
              <v-list-item @click="copyLink(item)">
                <LucideIcon name="copy" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Copy partner link</v-list-item-title>
              </v-list-item>
              <v-list-item @click="copyFixedLink(item)">
                <LucideIcon name="link" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Copy fixed link</v-list-item-title>
              </v-list-item>
              <!-- The fixed page URL, not the partner link: opening the partner
                   link from here would log a click with no partner on it. -->
              <v-list-item @click="openLink(item)">
                <LucideIcon name="external-link" :size="16" class="mr-2" />
                <v-list-item-title class="kf-menu">Preview page</v-list-item-title>
              </v-list-item>

              <v-divider />

              <v-list-item @click="askToggle(item)">
                <LucideIcon
                  :name="item.is_active ? 'circle-pause' : 'circle-play'"
                  :size="16"
                  class="mr-2"
                />
                <v-list-item-title class="kf-menu">
                  {{ item.is_active ? "Deactivate" : "Activate" }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="askDelete(item)">
                <LucideIcon name="circle-x" :size="16" class="mr-2" color="#C0392B" />
                <v-list-item-title class="kf-menu kf-menu--danger">Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </DataTable>

    <!-- -------------------------------------------------- add / edit campaign -->
    <!-- One dialog for both: `campaign` null means create. On create it stays
         open on its "here is your URL" step so the link can be copied, so the
         list is refreshed BEHIND it on `created` rather than on close -- the new
         row is already there when Done is pressed. An edit closes itself and
         reloads the page in place, since nothing new is handed over. -->
    <CampaignUrlDialog
      v-model="form_dialog"
      :campaign="editing"
      @created="reload"
      @saved="load"
    />

    <!-- --------------------------------------------------------- performance -->
    <CampaignPerformanceDialog v-model="performance_dialog" :campaign="performing" />

    <!-- ---------------------------------------------------------- deactivate -->
    <ConfirmDialog
      v-model="toggle_dialog"
      :title="toggleTitle"
      :confirm-text="target && target.is_active ? 'Deactivate' : 'Activate'"
      :icon="target && target.is_active ? 'circle-pause' : 'circle-play'"
      :loading="toggling"
      @cancel="toggle_dialog = false"
      @confirm="confirmToggle"
    >
      <template v-if="target && target.is_active">
        <strong>{{ target.name }}</strong> stops showing products. The link keeps
        working — anyone who opens it is told the campaign is no longer active.
        You can switch it back on at any time.
      </template>
      <template v-else-if="target">
        <strong>{{ target.name }}</strong> starts showing its
        {{ target.product_count }} product{{ target.product_count === 1 ? "" : "s" }}
        again to anyone who opens its link.
      </template>
    </ConfirmDialog>

    <!-- -------------------------------------------------------------- delete -->
    <ConfirmDialog
      v-model="delete_dialog"
      :title="target ? `Delete “${target.name}”?` : 'Delete campaign?'"
      confirm-text="Delete"
      tone="danger"
      icon="circle-x"
      :loading="deleting"
      @cancel="delete_dialog = false"
      @confirm="confirmDelete"
    >
      <template v-if="target">
        The campaign and its link are removed for good. Anyone who opens a link
        already shared will be told it is not valid. The
        {{ target.product_count }} product{{ target.product_count === 1 ? "" : "s" }}
        in it are not affected.
        <div class="kf-warn mt-2">This cannot be undone.</div>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script>
/**
 * Campaigns — every public campaign link, and the two ways to retire one.
 *
 * Deactivate and Delete are deliberately separate. Deactivating keeps the row
 * and the link and is reversible, which is what an admin wants when a campaign
 * ends; deleting kills a URL that may already be in someone's WhatsApp, and is
 * not recoverable. Collapsing them into one action would make the reversible
 * case as frightening as the permanent one.
 *
 * Edit reuses the create dialog and never touches the link. The campaign id is
 * signed into the token, so it is shown read-only; the name, the set of
 * products and the on/off switch can all change, and the URL an admin already
 * shared keeps resolving to the same campaign. That is the difference between
 * editing and deleting: a link that has been sent survives one and not the
 * other.
 */
import DataTable from "@/components/karrofin/DataTable.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import ConfirmDialog from "@/components/karrofin/ConfirmDialog.vue";
import CampaignUrlDialog from "@/components/karrofin/CampaignUrlDialog.vue";
import CampaignPerformanceDialog from "@/components/karrofin/CampaignPerformanceDialog.vue";
import api from "@/app-js/karrofin/api";
import fmt from "@/app-js/karrofin/format";
import { copyToClipboard } from "@/app-js/karrofin/clipboard";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";
import _ from "lodash";

// Campaign.destination, as the Link column names it.
const DESTINATION_LABELS = {
  website: "Website",
  app: "App (Play Store)",
  smart: "Smart (Android → app)",
};

export default {
  name: "KarroFinCampaigns",
  components: {
    DataTable,
    StatusChip,
    ConfirmDialog,
    CampaignUrlDialog,
    CampaignPerformanceDialog,
  },
  data() {
    return {
      fmt,
      loading: true,
      error: null,
      items: [],
      page: 1,
      totalPages: 1,
      totalCount: null,
      pageLength: PAGE_LENGTH,
      search: "",

      form_dialog: false,
      // The row the form dialog is editing, or null to create. Read by the
      // dialog when it opens, so it must be set BEFORE form_dialog is.
      editing: null,
      toggle_dialog: false,
      delete_dialog: false,
      // The row a confirm dialog is about. Held rather than passed, because
      // ConfirmDialog's @confirm carries no payload.
      target: null,
      toggling: false,
      deleting: false,
      performance_dialog: false,
      // The row the Performance dialog is about; set before opening it.
      performing: null,

      headers: [
        { text: "Campaign", value: "name" },
        { text: "Products", value: "product_count", align: "center", width: 90 },
        { text: "Status", value: "is_active", width: 110 },
        { text: "Partner link", value: "dynamic_url" },
        { text: "Clicks", value: "clicks", align: "end", width: 80 },
        { text: "Offer clicks", value: "offer_clicks", align: "end", width: 100 },
        { text: "Signups", value: "signups", align: "end", width: 90 },
        { text: "Created by", value: "created_by_name", width: 130 },
        { text: "Created", value: "created", width: 110 },
        { text: "", value: "actions", align: "end", width: 56 },
      ],
    };
  },
  computed: {
    toggleTitle() {
      if (!this.target) return "";
      return this.target.is_active
        ? `Deactivate “${this.target.name}”?`
        : `Activate “${this.target.name}”?`;
    },
  },
  created() {
    this.debouncedReload = _.debounce(this.reload, 400);
    this.load();
  },
  beforeDestroy() {
    if (this.debouncedReload) this.debouncedReload.cancel();
  },
  methods: {
    load() {
      this.loading = true;
      this.error = null;
      // Started off a resolved promise so a synchronous throw inside the call
      // becomes a rejection this chain can see -- otherwise a function missing
      // from api/index.js's default export map leaves the spinner running
      // forever with nothing in the network tab.
      Promise.resolve()
        .then(() =>
          api.getCampaigns({
            page_number: this.page,
            page_length: this.pageLength,
            search: this.search,
          })
        )
        .then((data) => {
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
        })
        .catch((err) => {
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openAdd() {
      this.editing = null;
      this.form_dialog = true;
    },
    openEdit(item) {
      this.editing = item;
      this.form_dialog = true;
    },
    copyLink(item) {
      copyToClipboard(item.dynamic_url).then((ok) => {
        this.showSnakeBar(
          ok ? "success" : "error",
          ok ? "Partner link copied" : "Could not copy — open Edit and copy it from there"
        );
      });
    },
    copyFixedLink(item) {
      copyToClipboard(item.url).then((ok) => {
        this.showSnakeBar(
          ok ? "success" : "error",
          ok ? "Fixed link copied" : "Could not copy — open the link and copy it from there"
        );
      });
    },
    destinationLabel(destination) {
      return DESTINATION_LABELS[destination] || DESTINATION_LABELS.website;
    },
    openPerformance(item) {
      this.performing = item;
      this.performance_dialog = true;
    },
    openLink(item) {
      window.open(item.url, "_blank");
    },

    /* ------------------------------------------------------------- confirms */
    askToggle(item) {
      this.target = item;
      this.toggle_dialog = true;
    },
    confirmToggle() {
      const next = !this.target.is_active;
      this.toggling = true;
      Promise.resolve()
        .then(() => api.setCampaignActive(this.target.id, next))
        .then(() => {
          this.showSnakeBar(
            "success",
            next ? "Campaign activated" : "Campaign deactivated"
          );
          // Closed inside .then on purpose: a failed write must leave the
          // dialog open with its error, not look as though it worked.
          this.toggle_dialog = false;
          this.load();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Could not update the campaign"
          );
        })
        .finally(() => {
          this.toggling = false;
        });
    },
    askDelete(item) {
      this.target = item;
      this.delete_dialog = true;
    },
    confirmDelete() {
      this.deleting = true;
      const name = this.target.name;
      Promise.resolve()
        .then(() => api.deleteCampaign(this.target.id))
        .then(() => {
          this.showSnakeBar("success", `“${name}” deleted`);
          this.delete_dialog = false;
          // Back to page 1: deleting the last row of the last page would
          // otherwise leave the table on a page that no longer exists.
          this.reload();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Could not delete the campaign"
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-inline-search {
  max-width: 220px;
}
.kf-name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}
.kf-sub {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  font-family: monospace;
}
.kf-link {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 260px;
}
.kf-link__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: monospace;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-body);
}
.kf-menu {
  font-size: var(--kf-fs-hint);
}
.kf-menu--danger {
  color: var(--kf-danger);
}
.kf-warn {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: var(--kf-danger);
}
</style>
